import { component } from "picoapp";
import { ANNOUNCEMENT_BAR_SETTINGS } from "../Carousels/tbsp-carousel--constants";

/**
 * Promo discount banner — two-path architecture (mirrors tbsp-announcement-bar.liquid):
 * Path 1 (SSR): data-promo-banner-mode="ssr" → static promo banner, skip TS init
 * Path 2 (Cookie fallback): [data-promo-allowlist] present → read discount_code cookie before Splide
 * Default: Splide carousel
 */
type PromoAllowlistEntry = {
  backgroundColor: string;
  code: string;
  messageTemplate: string;
  textColor: string;
};

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function insertPromoCode(template: string, code: string): string {
  return template.split("{code}").join(escapeHtml(code));
}

function getDiscountCodeCookie(): string | null {
  const match = document.cookie.match(/(?:^|; )discount_code=([^;]*)/);
  if (!match) {
    return null;
  }

  const code = decodeURIComponent(match[1]).trim();
  return code || null;
}

function findPromoEntryForCookie(
  root: HTMLElement,
  cookieCode: string
): PromoAllowlistEntry | null {
  const allowlist = root.querySelector("[data-promo-allowlist]");
  if (!allowlist) {
    return null;
  }

  const normalizedCookieCode = cookieCode.toLowerCase();

  for (const entry of allowlist.querySelectorAll("[data-promo-code]")) {
    const code = entry.getAttribute("data-promo-code") || "";
    if (code.toLowerCase() !== normalizedCookieCode) {
      continue;
    }

    return {
      code,
      backgroundColor: entry.getAttribute("data-background-color") || "",
      textColor: entry.getAttribute("data-text-color") || "",
      messageTemplate: entry.innerHTML.trim(),
    };
  }

  return null;
}

function showMessageArea(messageArea: HTMLElement, fadeIn: boolean): void {
  const reveal = () => {
    messageArea.classList.remove("opacity-0");
  };

  if (
    !fadeIn ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    reveal();
    return;
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(reveal);
  });
}

function applyPromoDiscountCookieFallback(root: HTMLElement): boolean {
  const cookieCode = getDiscountCodeCookie();
  if (!cookieCode) {
    return false;
  }

  const matchedEntry = findPromoEntryForCookie(root, cookieCode);
  if (!matchedEntry) {
    return false;
  }

  const carouselContent = root.querySelector<HTMLElement>(
    "[data-announcement-carousel-content]"
  );
  const banner = root.querySelector<HTMLElement>(
    "[data-promo-discount-banner]"
  );
  const textElement = root.querySelector<HTMLElement>(
    "[data-promo-discount-text]"
  );

  if (!carouselContent || !banner || !textElement) {
    return false;
  }

  if (matchedEntry.backgroundColor) {
    root.style.backgroundColor = matchedEntry.backgroundColor;
  }

  if (matchedEntry.textColor) {
    textElement.style.color = matchedEntry.textColor;
  }

  textElement.innerHTML = insertPromoCode(
    matchedEntry.messageTemplate,
    cookieCode
  );
  carouselContent.hidden = true;
  banner.hidden = false;
  root
    .querySelectorAll<HTMLElement>("[data-announcement-carousel-control]")
    .forEach((control) => {
      control.hidden = true;
    });
  root.classList.remove("splide");
  root.dataset.promoBannerMode = "cookie";

  return true;
}

export const announcementBar = component(async (node: HTMLElement) => {
  // Path 1 guard: SSR promo banner
  if (node.dataset.promoBannerMode === "ssr") {
    return;
  }

  const messageArea = node.querySelector<HTMLElement>(
    "[data-announcement-message-area]"
  );
  const hasPromoAllowlist = Boolean(
    node.querySelector("[data-promo-allowlist]")
  );

  // Path 2: cookie fallback before Splide
  if (hasPromoAllowlist && applyPromoDiscountCookieFallback(node)) {
    if (messageArea) {
      showMessageArea(messageArea, true);
    }
    return;
  }

  if (hasPromoAllowlist && messageArea) {
    showMessageArea(messageArea, false);
  }

  // Default: Splide carousel
  const areArrowsVisible = node.dataset.arrowsVisible === "true";

  const [{ default: Splide }] = await Promise.all([
    import("@splidejs/splide"),
    import("@splidejs/splide/css/core"),
  ]);

  const splide = new Splide(node, {
    ...ANNOUNCEMENT_BAR_SETTINGS,
    arrows: areArrowsVisible,
  });
  const updateBackgroundColor = (slide: HTMLElement) => {
    const backgroundColor = slide.getAttribute("data-background-color");
    if (backgroundColor) {
      node.style.backgroundColor = backgroundColor;
    } else {
      node.style.backgroundColor = "";
    }
  };

  // Fade the bar background when it changes with the slide
  node.style.transition = `background-color ${ANNOUNCEMENT_BAR_SETTINGS.speed}ms ease`;

  splide.on("visible", ({ slide }) => {
    slide.classList.remove("absolute", "invisible");
    const splideList = slide.closest(".splide__list");
    if (splideList) {
      splideList.classList.remove("justify-center");
    }
  });

  splide.on("moved", (newIndex) => {
    const slides = splide.Components.Slides.get();
    const currentSlide = slides[newIndex]?.slide;
    if (currentSlide) {
      updateBackgroundColor(currentSlide);
    }
  });

  splide.mount();

  // Apply initial background from first slide
  const initialSlide = splide.Components.Slides.get()[0]?.slide;
  if (initialSlide) {
    updateBackgroundColor(initialSlide);
  }

  const splideSlides = splide.Components.Slides.get();
  splideSlides.forEach((splideSlide) => {
    const { index, slide } = splideSlide;
    const announcementBarLinks = slide.querySelectorAll("a");
    announcementBarLinks.forEach((link) => {
      link.setAttribute("data-mtr-as", "clickPromoLink");
      link.setAttribute("data-mtr-track", "click");
      link.setAttribute("data-mtr-position", `${index}`);
    });
  });
});
